import styled from 'styled-components';
import { ellipsisMixin } from '@elfsight/widget-ui';
import { createReadableClass, getColorWithAlpha } from '@elfsight/widget-utils';
import tinycolor from 'tinycolor2';
import { Link } from '../../../lib/components';
import { useSocialFeedSettings } from '../../../utils';
import { useHeaderData } from '../../helpers';

interface NameProps {
  variant: 'name' | 'username';
}

export function HeaderName({ variant }: NameProps) {
  const { headerTextColor = '' } = useSocialFeedSettings();
  const { url = '', name = '', username = '' } = useHeaderData();

  const shouldUseNameComponent = variant === 'name';
  const Component = shouldUseNameComponent ? Name : Username;

  const text = shouldUseNameComponent ? name : username;
  const normalizedText = text.trim();

  const currentAlpha = tinycolor(headerTextColor).getAlpha();
  const finalAlpha = currentAlpha * 0.7;
  const colorWithAlpha = getColorWithAlpha(headerTextColor, finalAlpha);

  if (!normalizedText) {
    return null;
  }

  const ariaLabel = `Visit ${normalizedText}'s profile`;

  return (
    <Component
      href={url}
      $color={shouldUseNameComponent ? headerTextColor : colorWithAlpha}
      aria-label={ariaLabel}
    >
      {normalizedText}
    </Component>
  );
}

const Name = styled(Link).attrs({
  className: createReadableClass({
    component: 'header-names',
    element: 'name'
  })
})<{
  $color?: string;
}>`
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  flex-grow: 0;
  color: ${({ $color }) => $color};
  overflow: hidden;
  ${ellipsisMixin};

  &:hover {
    text-decoration: underline;
  }
`;

const Username = styled(Link).attrs({
  className: createReadableClass({
    component: 'header-names',
    element: 'username'
  })
})<{
  $color?: string;
}>`
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  color: ${({ $color }) => $color};
  overflow: hidden;
  ${ellipsisMixin};

  &:hover {
    text-decoration: underline;
  }
`;
